import { useMemo } from 'react';
import type { DateRange, HeatmapBarSegment, HeatmapCriticalPeriodsBlock } from './types';
import { mergeOverlappingPeriods } from './helpers';

type CriticalPeriod = { id: string; title?: string; startDate: string; endDate: string };
type CriticalPeriodsSource = { criticalPeriods?: CriticalPeriod[] };

interface UseHeatmapCriticalPeriodsParams {
  project: CriticalPeriodsSource | null;
  dateRange: DateRange | null;
}

const overlapsRange = (periodStart: string, periodEnd: string, range: DateRange): boolean => {
  return periodStart <= range.end && periodEnd >= range.start;
};

const buildCriticalPeriods = (periods: CriticalPeriod[] | undefined, range: DateRange): HeatmapBarSegment[] => {
  const filtered = (periods ?? []).filter(
    p => p.startDate && p.endDate && overlapsRange(p.startDate, p.endDate, range),
  );
  const merged = mergeOverlappingPeriods(filtered.map(p => ({ startDate: p.startDate, endDate: p.endDate })));
  return merged.map((rangeItem, index) => ({
    id: `critical_${index}`,
    startDate: rangeItem.startDate,
    endDate: rangeItem.endDate,
    color: 'error.main' as const,
  }));
};

export const useHeatmapCriticalPeriods = ({
  project,
  dateRange,
}: UseHeatmapCriticalPeriodsParams): { criticalPeriodsBlock: HeatmapCriticalPeriodsBlock } => {
  const criticalPeriodsBlock = useMemo(() => {
    if (!project || !dateRange) return { bars: [] };
    return { bars: buildCriticalPeriods(project.criticalPeriods, dateRange) };
  }, [project, dateRange]);

  return { criticalPeriodsBlock };
};
